import { createApi } from '@reduxjs/toolkit/query/react';

import { CRM_TASKS, DELETE, GET, POST } from '@app/constants';
import { customBaseQuery } from '@app/services/rtkQuery/baseQuery';
import type { NCrmTasks } from '@app/services/rtkQuery/CrmTasks/types';
import type { NActionsHistory, NAuth } from '@app/types';

export const crmTasksApi = createApi({
  reducerPath: 'crmTasksApi',
  baseQuery: ({ url, ...props }) =>
    customBaseQuery({ url: `${CRM_TASKS}/${url}`, ...props }),
  tagTypes: ['ActionHistory', 'CrmTaskTypes'],
  endpoints: ({ query, mutation }) => ({
    GetTask: query<NCrmTasks.GetTaskResponse, NCrmTasks.GetTaskRequest>({
      query: ({ id, ...params }) => ({
        url: id,
        method: GET,
        params,
      }),
    }),
    DeleteTask: mutation<void, NCrmTasks.DeleteTaskRequest>({
      query: ({ id, ...params }) => ({
        url: id,
        method: DELETE,
        params,
      }),
      invalidatesTags: ['ActionHistory'],
    }),
    DeleteTasksList: mutation<void, NCrmTasks.DeleteTasksListRequest>({
      query: ({ ActiveBranchId, ...data }) => ({
        url: 'DeleteCrmTasks',
        method: POST,
        data,
        params: { ActiveBranchId },
      }),
    }),
    CreateTask: mutation<
      NCrmTasks.ModelCrmTask,
      {
        data: NCrmTasks.ModelCrmTaskCreate;
        activeBranchId: NAuth.UserPrint['activeBranchId'];
      }
    >({
      query: ({ activeBranchId, data }) => ({
        url: 'CreateCrmTask',
        method: POST,
        data,
        params: { ActiveBranchId: activeBranchId },
      }),
      invalidatesTags: ['ActionHistory'],
    }),
    GetCrmTaskFilterData: query<
      NCrmTasks.ModelCrmTaskFilterData,
      NCrmTasks.ModelCrmTaskFilterRequest
    >({
      query: ({ logs, ...params }) => ({
        url: 'GetCrmTaskFilterData',
        method: GET,
        logs,
        params,
      }),
    }),
    SearchCrmTasks: mutation<
      NCrmTasks.ModelCrmTaskSearchResult,
      NCrmTasks.ModelCrmTaskSearchRequest
    >({
      query: ({ data, ...params }) => ({
        url: 'SearchCrmTasks',
        method: POST,
        params,
        data,
      }),
    }),
    SearchCrmTaskUsers: query<
      NCrmTasks.ModelCrmTaskUser[],
      NCrmTasks.SearchCrmTaskUsersRequest
    >({
      query: (params) => ({
        url: 'SearchCrmTaskUsers',
        method: GET,
        params,
      }),
    }),
    SearchCrmTaskClients: query<
      NCrmTasks.ModelCrmTaskClient[],
      NCrmTasks.SearchCrmTaskClientsRequest
    >({
      query: (params) => ({
        url: 'SearchCrmTaskClients',
        method: GET,
        params,
      }),
    }),
    SetCompletedForCrmTasks: mutation<
      NCrmTasks.ModelCrmTask,
      {
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
        CrmTaskIds: number[];
      }
    >({
      query: ({ ActiveBranchId, ...data }) => ({
        url: 'SetCompletedForCrmTasks',
        method: POST,
        params: { ActiveBranchId },
        data,
      }),
    }),
    GetSearchCrmTasks: query<
      NCrmTasks.ModelCrmTaskSearchResult,
      {
        searchId: number;
        page: number;
        rowPerPage: number;
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
      }
    >({
      query: (params) => ({
        url: 'GetSearchCrmTasks',
        method: GET,
        params,
      }),
    }),
    UpdateCrmTask: mutation<
      NCrmTasks.ModelCrmTask,
      {
        data: Partial<NCrmTasks.ModelCrmTaskUpdate>;
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
      }
    >({
      query: ({ ActiveBranchId, data }) => ({
        url: 'UpdateCrmTask',
        method: POST,
        params: { ActiveBranchId },
        data,
      }),
      invalidatesTags: ['ActionHistory'],
    }),
    CompletedCrmTask: mutation<
      NCrmTasks.ModelCrmTask,
      {
        id: number;
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
      }
    >({
      query: ({ id, ...params }) => ({
        url: `CompletedCrmTask/${id}`,
        method: POST,
        params,
      }),
      invalidatesTags: ['ActionHistory'],
    }),
    SetOwnersForCrmTasks: mutation<
      NCrmTasks.ModelCrmTask,
      {
        data: NCrmTasks.ModelBulkCrmTasksSetOwners;
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
      }
    >({
      query: ({ ActiveBranchId, data }) => ({
        url: 'SetOwnersForCrmTasks',
        method: POST,
        params: { ActiveBranchId },
        data,
      }),
    }),
    GetCrmTasksForPeriod: mutation<
      NCrmTasks.GetTasksForPeriodResponse,
      NCrmTasks.GetTasksForPeriodRequest
    >({
      query: ({ ActiveBranchId, ...body }) => ({
        url: 'GetCrmTasksForPeriod',
        method: POST,
        params: { ActiveBranchId },
        data: { ...body },
      }),
      transformErrorResponse: ({ data }) => data,
    }),
    CreateCrmTaskComment: mutation<
      NCrmTasks.ModelCrmTaskComment,
      {
        body: NCrmTasks.ModelCrmTaskCommentCreate;
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
      }
    >({
      query: ({ ActiveBranchId, body }) => ({
        url: 'CreateCrmTaskComment',
        method: POST,
        params: { ActiveBranchId },
        data: { ...body },
      }),
    }),
    GetCrmTask: query<
      NCrmTasks.ModelCrmTask,
      {
        id: number;
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
      }
    >({
      query: ({ id, ActiveBranchId }) => ({
        url: `${id}`,
        method: GET,
        params: { ActiveBranchId },
      }),
    }),
    InProgressCrmTask: mutation<
      void,
      {
        id: number;
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
      }
    >({
      query: ({ ActiveBranchId, id }) => ({
        url: `InProgressCrmTask/${id}`,
        method: POST,
        params: { ActiveBranchId },
      }),
      invalidatesTags: ['ActionHistory'],
    }),
    GetCrmTaskHistoryById: query<
      NActionsHistory.ModelActionHistory[],
      {
        id: number;
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
      }
    >({
      query: ({ id, ActiveBranchId }) => ({
        url: `GetCrmTaskHistoryById/${id}`,
        method: GET,
        params: { ActiveBranchId },
      }),
    }),
    GetCrmTaskTypes: query<
      NCrmTasks.ModelCrmTaskType[],
      {
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
        includeId?: number;
      }
    >({
      query: (params) => ({
        url: 'GetCrmTaskTypes',
        method: GET,
        params,
      }),
      providesTags: ['CrmTaskTypes'],
    }),
    CreateCrmTaskType: mutation<
      NCrmTasks.ModelCrmTaskType,
      {
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
        data: NCrmTasks.ModelCrmTaskType;
      }
    >({
      query: ({ ActiveBranchId, data }) => ({
        url: 'CreateCrmTaskType',
        method: POST,
        params: { ActiveBranchId },
        data,
      }),
      invalidatesTags: ['CrmTaskTypes'],
    }),
  }),
});

export const {
  useGetTaskQuery,
  useCreateTaskMutation,
  useDeleteTaskMutation,
  useDeleteTasksListMutation,
  useGetCrmTaskFilterDataQuery,
  useSearchCrmTasksMutation,
  useSearchCrmTaskUsersQuery,
  useSearchCrmTaskClientsQuery,
  useSetCompletedForCrmTasksMutation,
  useLazyGetSearchCrmTasksQuery,
  useLazySearchCrmTaskUsersQuery,
  useLazySearchCrmTaskClientsQuery,
  useUpdateCrmTaskMutation,
  useCompletedCrmTaskMutation,
  useSetOwnersForCrmTasksMutation,
  useGetCrmTasksForPeriodMutation,
  useCreateCrmTaskCommentMutation,
  useLazyGetCrmTaskQuery,
  useInProgressCrmTaskMutation,
  useLazyGetCrmTaskHistoryByIdQuery,
  useGetCrmTaskHistoryByIdQuery,
  useGetCrmTaskTypesQuery,
  useLazyGetCrmTaskTypesQuery,
  useCreateCrmTaskTypeMutation,
} = crmTasksApi;
